import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { EditorContent, type JSONContent } from "@tiptap/react";
import { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useEditor } from "@/editor/editor";
import type { termosCreateMutation } from "./__generated__/termosCreateMutation.graphql";
import type { termosQuery } from "./__generated__/termosQuery.graphql";

export const Route = createFileRoute("/_logged/c/$club/(standalone)/termos")({
	component: RouteComponent,
});

function RouteComponent() {
	const { club, session } = useRouteContext({ from: "/_logged/c/$club" });
	const data = useLazyLoadQuery<termosQuery>(
		graphql`
			query termosQuery($club: String!) {
				club(slug: $club) {
					id
					termsOfService(status: ALL) {
						id
					active
						content
					}
				}
			}
		`,
		{ club: club.slug },
	);
	const terms = data.club.termsOfService;
	const [active, setActive] = useState(terms?.active ?? false);
	const editor = useEditor({
		club: club.slug,
		session,
		editable: true,
		content: (terms?.content as JSONContent | null) ?? undefined,
		placeholder: "Escreva os termos de uso da sua comunidade aqui...",
		features: { styling: { enabled: true } },
	});
	const [createTerms, isPending] = useMutation<termosCreateMutation>(graphql`
		mutation termosCreateMutation($input: CreateTermsOfServiceInput!) {
			createTermsOfService(createTermsOfServiceInput: $input) {
				id
				active
				content
			}
		}
	`);

	const handleSave = () => {
		if (!editor || editor.isEmpty) {
			toast.error("Escreva os termos de uso antes de salvar.");
			return;
		}
		createTerms({
			variables: {
				input: { club: club.slug, active, content: editor.getJSON() },
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Termos de uso atualizados com sucesso!");
			},
			onError: () => {
				toast.error("Não foi possível salvar os termos de uso.");
			},
			updater: (store) => store.get(data.club.id)?.invalidateRecord(),
		});
	};

	return (
		<div className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
			<div className="flex flex-wrap items-center justify-between gap-4">
				<Heading>Termos de Uso</Heading>
				<div className="flex items-center gap-4">
					<div className="flex items-center gap-2 text-sm">
						Ligado
						<Switch
							aria-label="Ativar termos de uso"
							checked={active}
							onCheckedChange={setActive}
						/>
					</div>
					<Button onClick={handleSave} disabled={isPending || !editor}>
						{isPending ? "Salvando..." : "Salvar alterações"}
					</Button>
				</div>
			</div>
			<div className="rounded-md border">
				<div className="flex gap-2 border-b p-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => editor?.commands.undo()}
						aria-label="Desfazer"
					>
						Desfazer
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => editor?.commands.redo()}
						aria-label="Refazer"
					>
						Refazer
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => editor?.chain().focus().toggleBold().run()}
						aria-label="Negrito"
					>
						Negrito
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => editor?.chain().focus().toggleItalic().run()}
						aria-label="Itálico"
					>
						Itálico
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => editor?.chain().focus().toggleUnderline().run()}
						aria-label="Sublinhado"
					>
						Sublinhado
					</Button>
				</div>
				<EditorContent
					editor={editor}
					className="min-h-96 p-4 [&_.ProseMirror]:min-h-80 [&_.ProseMirror]:outline-none"
				/>
			</div>
		</div>
	);
}
